import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { resolvePlacedPhotoIdentity } from '@/lib/photos/fill-shape-with-photo'
import { pageSidesOfShape } from '@/lib/photos/photo-placement-geometry'
import {
  type PlacementSidesOnCanvas,
  observePlacementsOnSpread,
  reconcilePlacementOverrides,
  setPhotoPlacementOverride,
} from '@/lib/photos/photo-placement-session'
import type { PageSide } from '@/lib/photos/types/page-side'
import type { SpreadBleedSize } from './types/spread-bleed-size'

/**
 * Candids whose photo shapes currently touch a page of the spread, each with the pages (left /
 * right) it touches, merged across its copies. Includes kindId 4 image shapes and photo-filled
 * shapes. Pasteboard-only placements are excluded, and so is a single-page spread's grey half.
 */
export function collectPlacedPhotoSides(
  shapes: ShapeData[],
  spread: SpreadBleedSize
): Map<string, Set<PageSide>> {
  const placed = new Map<string, Set<PageSide>>()
  const { bleedWidth, bleedHeight, singlePageSide } = spread

  for (const shape of shapes) {
    const candidId = resolvePlacedPhotoIdentity(shape)?.imageId

    if (!candidId) continue

    const sides = pageSidesOfShape(shape, bleedWidth, bleedHeight, singlePageSide)

    if (sides.size === 0) continue

    const known = placed.get(candidId)

    if (known) {
      for (const side of sides) known.add(side)
    } else {
      placed.set(candidId, sides)
    }
  }

  return placed
}

/** After an image/photo-fill shape is removed, clear tray placed state for `spreadId` when no
 *  on-spread copies remain there — a copy this session placed on some OTHER spread is untouched.
 *  A copy that does remain may sit on fewer pages than before, so the pages are re-derived. */
export function syncPhotoPlacementOverrideAfterShapeRemoved(
  removedShape: ShapeData,
  remainingShapes: ShapeData[],
  spread: SpreadBleedSize,
  spreadId: string
): void {
  const candidId = resolvePlacedPhotoIdentity(removedShape)?.imageId

  if (!candidId) return

  const remaining = collectPlacedPhotoSides(remainingShapes, spread)

  if (remaining.has(candidId)) {
    reconcilePlacementOverrides(remaining, spreadId)
  } else {
    setPhotoPlacementOverride(candidId, false, spreadId)
  }
}

/** Align `spreadId`'s session overrides with photo shapes currently touching it (undo/redo/
 *  move) — only ever touches that spread's own slice of the session-wide placement tracking. */
export function reconcilePhotoPlacementOverridesWithShapes(
  shapes: ShapeData[],
  spread: SpreadBleedSize,
  spreadId: string
): void {
  reconcilePlacementOverrides(collectPlacedPhotoSides(shapes, spread), spreadId)
}

/** Seeds `spreadId`'s session placements from the photos on it as it first loads. See
 *  `observePlacementsOnSpread`. */
export function observePhotoPlacementsFromShapes(
  shapes: ShapeData[],
  spread: SpreadBleedSize,
  spreadId: string
): void {
  const onCanvas: PlacementSidesOnCanvas = collectPlacedPhotoSides(shapes, spread)

  observePlacementsOnSpread(onCanvas, spreadId)
}
